import axios from "axios";
import { enqueueSnackbar } from "notistack";
import { store } from "../../index.js";
import { resetAuthState } from "../features/permission/permission.js";
import { resetAccountDetails } from "../features/account/account.js";
import { resetAppState } from "../features/app/app.js";

const API_BASE_URL = process.env.REACT_APP_BETA;
export const devmode = window.location.hostname !== 'product.frejun.com';



const urls = {
  "/api/v1/core/send-call-logs/": "Report Downloaded",
  "/api/v1/auth/update-business-hours/": "Working Hours Set",
  "/api/v1/auth/add-users/": "New User Added",
  "/api/v1/core/create-message-template/": "Message Template Created",
  "/api/v1/calls/initiate-call/": "Call Initiated",
};

const unsafePaths = [
  "/login",
  "/interviews",
  "/forgot-password",
  "/signup",
  "/deny",
  "/invite",
  "/call-pricing-inr",
  "/call-pricing-usd",
];

const validateStatus = (status) => status >= 200 && status <= 500;

export const JService = {
  get(url, params = {}, config) {
    return jwtInstance
      .get(url, { params: params, ...config })
      .then((res) => res)
      .catch((reason) => Promise.reject(reason));
  },

  post(url, data) {
    return jwtInstance
      .post(url, data)
      .then((res) => res)
      .catch((reason) => Promise.reject(reason));
  },

  patch(url, data) {
    return jwtInstance
      .patch(url, data)
      .then((res) => res)
      .catch((reason) => Promise.reject(reason));
  },

  put(url, data) {
    return jwtInstance
      .put(url, data)
      .then((res) => res)
      .catch((reason) => Promise.reject(reason));
  },

  delete(url, data = {}) {
    return jwtInstance
      .delete(url, { data: data })
      .then((res) => res)
      .catch((reason) => Promise.reject(reason));
  },

  awaitAll() {
    return axios
      .all(Array.from(arguments))
      .then(axios.spread((...responses) => responses))
      .catch((reasons) => Promise.reject(reasons));
  },
};

const jwtInstance = axios.create({
  baseURL: process.env.REACT_APP_BETA,
});

jwtInstance.interceptors.request.use(
  async (config) => {
    const req = {
      ...config,
      validateStatus,
    };

    if (["patch", "delete", "post"].includes(req.method)) {
      req.headers["content-type"] = "application/json";
      req.headers["Accept"] = "application/json";
    }

    req.withCredentials = true;
    return req;
  },
  (error) => {
    Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

jwtInstance.interceptors.response.use(
  async (res) => {
    if (res.status === 401) {
      if (res?.config && !res?.config?.url?.includes("/oauth/")) {
        if (!res.config._retry) {
          res.config._retry = true;
          return retryRequest(res.config);
        }
      }  
    }
    if (res.status === 204) {
      return { success: true };
    }
    if (
      res.status >= 400 ||
      ("data" in res &&
        typeof res.data === "object" &&
        "success" in res.data &&
        !res.data.success)
    ) {
      const formattedError = findErrorString(
        res.data.message ?? "Something went wrong"
      );
      return Promise.reject({ success: false, message: formattedError, status: res.status });
    }
    return res?.data ?? res;
  },

  // Any status codes that falls outside the range defined in validateStatus
  async function (error) {
    console.log(
      "interceptor recieved response with invalid (non-2xx) status",
      error
    );
    if (error.code === "ERR_CANCELED") return {};
    if (error?.stack?.includes("InvalidTokenError") && error?.config) {
      if (!error.config._retry) {
        error.config._retry = true;
        return retryRequest(error.config);
      }
    }
    const formattedError = formatError(error);
    return Promise.reject(formattedError);
  }
);

async function retryRequest(config) {
  if (
    unsafePaths.includes(window.location.pathname) ||
    window.location.pathname === "/" ||
    config?.url?.includes("/logout")
  ) {
    console.log("cancelling call to refresh access token");
    return;
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(() => {
        if (
          unsafePaths.includes(window.location.pathname) ||
          window.location.pathname === "/" ||
          config?.url?.includes("/logout")
        ) {
          return;
        }
        return jwtInstance(config);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  isRefreshing = true;

  try {
    const refreshSuccess = await refreshAccessToken();
    if (refreshSuccess) {
      console.log("refresh successful config", config);
      isRefreshing = false;
      processQueue(null);
      return jwtInstance(config);
    } else {
      throw new Error("Token refresh failed");
    }
  } catch (error) {
    isRefreshing = false;
    processQueue(error);
    enqueueSnackbar("Request failed. All tokens expired, please login again", {
      variant: "error",
    });
    safeLogout();
    return Promise.reject({
      success: false,
      message: "Request failed. All tokens expired, please login again",
    });
  }
}

function findErrorString(err) {
  const genericMessage = "Something went wrong";
  if (typeof err === "string") return err;
  if (typeof err === "object" && Array.isArray(err)) {
    if (err.length > 0) return findErrorString(err[0]);
    else return genericMessage;
  }
  if (typeof err === "object") {
    if (Object.keys(err)?.length > 0)
      return findErrorString(Object.values(err)[0]);
    else return genericMessage;
  }
  return genericMessage;
}

function formatError(error) {
  const errObj = {
    success: false,
  };
  if (
    error?.response?.data &&
    typeof error.response.data === "object" &&
    "message" in error.response.data
  ) {
    const message = error.response.data.message;
    errObj["message"] = findErrorString(message);
  } else {
    errObj["message"] = "Something went wrong";
  }
  return errObj;
}

export async function refreshAccessToken() {
  const refreshResponse = await fetch(
    process.env.REACT_APP_BETA + "/api/v1/auth/token/refresh/?webapp=True",
    {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    }
  );
  return refreshResponse.status === 200;
}

export async function safeLogout(sendLogoutRequest = true) {
  try {
    const avoid = [
      "length",
      "clear",
      "getItem",
      "key",
      "removeItem",
      "setItem",
      "removeItem",
    ];
    for (const x in localStorage) {
      if (
        !x ||
        x.includes("WZRK") ||
        avoid.includes(x) ||
        x.includes("redirect")
      )
        continue;
      localStorage.removeItem(x);
    }
    if (sendLogoutRequest) {
      const res = await JService.get("/api/v1/auth/logout/?webapp=true");
      window.productFruits?.services?.destroy();
      console.log("safelogout res", res);
    }
    if (!["/login", "/"].includes(window.location.pathname)) {
      setTimeout(() => {
        window.location.replace("/login");
        store.dispatch(resetAuthState());
        store.dispatch(resetAccountDetails());
        store.dispatch(resetAppState());
      }, 300);
    }
  } catch (error) {
    console.error(error);
  }
}

export async function validateEmailDomain(email) {
  const adminUrl = process.env.REACT_APP_ADMIN_PLATFORM_URL;
  if (!adminUrl) return { success: true, data: { valid: true } };

  try {
    const response = await axios.post(
      `${adminUrl}/controls/validate-email`,
      { email },
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data;
  } catch (error) {
    // Fail open: handle errors (500, network error, blocked by clien, whitelisting etc.)
    return { success: true, data: { valid: true } };
  }
}
